/**
 * EL CAMPO «FOCO» DE LA CONFIRMACIÓN, sin React: qué llega elegido, qué opciones
 * se enseñan, cuándo se puede confirmar y cómo se anota lo que pasó.
 *
 * La regla de QUÉ se propone vive en `focusLinking.ts`. Esto es solo el paso
 * siguiente: convertir esa propuesta en un formulario que cumpla lo que decidió
 * el usuario el 21-09-2026 —
 *
 *  · caso claro  → llega RELLENO y se puede cambiar;
 *  · caso dudoso → NO llega nada elegido y no se puede confirmar sin elegir;
 *  · otra plaga en la misma línea → se avisa y se elige: «son cosas distintas»
 *    (foco nuevo, conviven) o «es el mismo foco» (este muestreo adopta su plaga);
 *  · nunca se guarda un enlace que quien confirma no haya tenido delante.
 */
import type { Candidate, LinkProposal, LinkResult } from "@/lib/health/focusLinking";

export type FocusChoice =
  | { kind: "unset" }
  | { kind: "new" }
  | { kind: "join"; focusId: string; adopt: boolean };

export type ConfirmMode = "prefilled_accepted" | "changed" | "new" | "forced_choice";

export type FieldState = "prefilled" | "reopen" | "must_choose" | "other_pest" | "new";

export interface FocusOption {
  key: string;
  choice: FocusChoice;
  /** El candidato que hay detrás. `null` en «foco nuevo». */
  candidate: Candidate | null;
}

const SEVERITY_RANK: Record<string, number> = { low: 1, medium: 2, high: 3, critical: 4 };

/** Lo que llega elegido al abrir. Solo lo claro (unir) y el rebrote limpio (reabrir). */
export function initialChoice(proposal: LinkProposal): FocusChoice {
  if (proposal.kind === "join" || proposal.kind === "reopen") return { kind: "join", focusId: proposal.focusId, adopt: false };
  if (proposal.kind === "new") return { kind: "new" };
  return { kind: "unset" };
}

export function fieldStateOf(proposal: LinkProposal): FieldState {
  if (proposal.kind === "join") return "prefilled";
  return proposal.kind;
}

export const sameChoice = (a: FocusChoice, b: FocusChoice): boolean =>
  a.kind === b.kind && (a.kind !== "join" || (b.kind === "join" && a.focusId === b.focusId && a.adopt === b.adopt));

/**
 * Las opciones del campo, en el orden de la regla. Unirse a un foco de OTRA
 * plaga es siempre «adoptar su plaga»: no existe un foco con dos plagas.
 */
export function optionsOf(result: LinkResult): FocusOption[] {
  const options: FocusOption[] = result.candidates.map((c) => ({
    key: c.focus.id,
    choice: { kind: "join", focusId: c.focus.id, adopt: c.tier === "other_pest" },
    candidate: c,
  }));
  options.push({ key: "new", choice: { kind: "new" }, candidate: null });
  return options;
}

/**
 * Cómo llegó quien confirma a su elección. Es la telemetría con la que se
 * reafinan los umbrales: si lo relleno se cambia a menudo, la regla propone mal;
 * si NUNCA se cambia, a lo mejor nadie lo está mirando.
 */
export function modeOf(proposal: LinkProposal, choice: FocusChoice): ConfirmMode {
  if (proposal.kind === "must_choose" || proposal.kind === "other_pest") return "forced_choice";
  if (proposal.kind === "new") return choice.kind === "new" ? "new" : "changed";
  return sameChoice(choice, initialChoice(proposal)) ? "prefilled_accepted" : "changed";
}

/**
 * «Mejor» con la gravedad subiendo, o «peor» con la gravedad bajando: no se
 * impide —quien estaba delante de la planta sabe más que dos desplegables—,
 * pero se avisa, porque la marca ▲▼ del mapa sale del veredicto.
 */
export function verdictConflict(
  verdict: string | null,
  severity: string | null,
  previousSeverity: string | null,
): "better_but_higher" | "worse_but_lower" | null {
  const now = severity ? SEVERITY_RANK[severity] : undefined;
  const before = previousSeverity ? SEVERITY_RANK[previousSeverity] : undefined;
  if (!verdict || now == null || before == null) return null;
  if (verdict === "better" && now > before) return "better_but_higher";
  if (verdict === "worse" && now < before) return "worse_but_lower";
  return null;
}

/**
 * D (decisión de Pablo, 23-09-2026): el muestreo se vio ANTES de que se cerrara el
 * foco elegido (un parte confirmado días después). Devuelve el día del cierre, o
 * `null` si no es el caso. Entonces quien confirma elige: reabrir el foco con él, o
 * que entre ya cerrado. Misma comparación que la base (`confirm_scouting_session`):
 * fecha del parte ESTRICTAMENTE anterior al día del cierre; el mismo día o después
 * es un rebrote, que ya reabre solo.
 */
export function closedBeforeSampling(chosen: Candidate | null, samplingDate: string | null | undefined): string | null {
  const f = chosen?.focus;
  if (!f || f.status !== "resolved" || !f.resolvedOn || !samplingDate) return null;
  return samplingDate < f.resolvedOn ? f.resolvedOn : null;
}

/** Lo que contesta quien confirma a «el foco ya está cerrado, ¿lo reabres?». */
export type ClosedFocusChoice = "reopen" | "keep_closed";

export interface ConfirmReadiness {
  pestId: string | null;
  severity: string | null;
  choice: FocusChoice;
  /** El parte tiene sector: sin él no hay foco posible y se confirma sin enlazar. */
  hasBlock: boolean;
  /** Los candidatos aún se están pidiendo, o no se pudieron pedir. */
  proposalReady: boolean;
  /** El foco elegido se cerró después de este muestreo y aún no se ha contestado si reabrirlo. */
  closedUnanswered?: boolean;
}

export type BlockedBy = "pest" | "severity" | "focus" | "loading" | "closed" | null;

/** Qué falta para poder confirmar. `null` = nada. */
export function blockedBy(r: ConfirmReadiness): BlockedBy {
  if (!r.pestId && !(r.choice.kind === "join" && r.choice.adopt)) return "pest";
  if (!r.severity) return "severity";
  if (!r.hasBlock) return null;
  if (!r.proposalReady) return "loading";
  if (r.choice.kind === "unset") return "focus";
  if (r.closedUnanswered) return "closed";
  return null;
}
